import isNil from 'lodash/isNil'
import intersection from 'lodash/intersection'
import { List } from 'immutable'
import { AUTO_MODE_VALUE, OFF_MODE_VALUE } from './subtitles-menu-mode'

/**
 * @typedef SubtitleMenuStateOptions
 * @type {Object}
 * @property {import('./video-media-center-plugin.js').UserLanguages} [userLanguages]
 * @property {String} [restoredValue]
 * @property {String} [appLanguage]
 * @property {String} [audioTrackLanguage]
 * @property {string[]} [textTracks]
 */

/** @todo replace private fields and methods with native, after babelrc will be updated */
// eslint-disable-next-line import/prefer-default-export
export class SubtitlesMenuState {
  #data

  /**
   * Create a SubtitlesMenuState
   * The class is responsible for managing the selection logic and relationships between menu items
   * @param  {SubtitleMenuStateOptions} [options]
   */
  constructor (options = {}) {
    const { restoredValue } = options
    this.#data = this.#getSafeData(options)
    this.select(restoredValue)
  }

  #selectedMenuItems = []

  /**
   * get safe values for data from constructor options
   * @param {SubtitleMenuStateOptions} options
   */
  // eslint-disable-next-line class-methods-use-this
  #getSafeData (options) {
    const {
      userLanguages, appLanguage, audioTrackLanguage, textTracks,
    } = options
    return {
      userLanguages: List.isList(userLanguages) ? userLanguages : List(),
      appLanguage,
      audioTrackLanguage,
      textTracks: Array.isArray(textTracks) ? textTracks : [],
    }
  }

  /**
   * get first language from the user languages
   * @returns {string}
   */
  #getPreferableTextTrackThatUserUnderstand () {
    const { userLanguages, textTracks } = this.#data
    const langOptions = intersection(userLanguages.toJS(), textTracks)
    return langOptions[0]
  }

  /**
   * @returns {boolean}
   */
  #isTextTrackExistForLanguage (lang) {
    const { textTracks } = this.#data
    return textTracks.includes(lang)
  }

  /**
   * manage items selection for auto mode
   * @returns {void}
   */
  #selectAutoMode () {
    const { appLanguage, audioTrackLanguage } = this.#data

    const nextSelectedMenuItems = [AUTO_MODE_VALUE]

    if (audioTrackLanguage === appLanguage) {
      nextSelectedMenuItems.push(OFF_MODE_VALUE)
    } else {
      const textTrackForUserLanguage = this.#getPreferableTextTrackThatUserUnderstand()
      const valueToPush = isNil(textTrackForUserLanguage)
        ? OFF_MODE_VALUE
        : textTrackForUserLanguage
      nextSelectedMenuItems.push(valueToPush)
    }

    this.#selectedMenuItems = nextSelectedMenuItems
  }

  /**
   * select value:
   * 1. "auto" mode - "auto" and default value should be selected
   * 2. "value" mode - should select the specified value if there is
   * a text track for it, or the value equal to 'off'
   *
   * @param {string} value
   * @returns {void}
   */
  select (value) {
    if (isNil(value) || value === AUTO_MODE_VALUE) {
      this.#selectAutoMode()
    } else if (
      value === OFF_MODE_VALUE
        || this.#isTextTrackExistForLanguage(value)
    ) {
      this.#selectedMenuItems = [value]
    } else {
      this.select(AUTO_MODE_VALUE)
    }
  }

  /**
   * @returns {string[]}
   */
  getSelectedItems () {
    return this.#selectedMenuItems
  }

  /**
   * @returns {string}
   */
  // eslint-disable-next-line class-methods-use-this
  getOffModeValue () {
    return OFF_MODE_VALUE
  }
}
